import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button, Input, MultiSelect, Select } from '@baseV2'
import styled from 'styled-components'
import { useEffect, useMemo, useState } from 'react'
import { ADMIN_SOURCE, ADMIN_SUB_ROLES, CountryCodes, enquiryPlatform, MB_ROLES, REGION_TYPES, strings, SUPER_SUPER_USER_ID } from '@constants'
import { BiFieldWrapper, EditDetailWrapper, GridListWrapper } from '../styles'
import { Spacings } from '@styles'
import { useDispatch, useSelector } from 'react-redux'
import { createUser, getAllCommodityApi } from '@data'
import { isSuperAdmin, isSuperAdminOnly } from 'src/utils/adminFilter'
import { toggleDrawer } from '@data/state/action/root'
import { showLoader, showToast } from '@data/state/action'
import { getCountryDialCode } from '@utils'
import { EMPLOYEE_CODE_PATTERN, EMPLOYEE_CODE_PLACEHOLDER, EMPLOYEE_CODE_REGEX } from './employeeCode'

const deriveStateFromProp = (user) => {
	const _state = {
		full_name: {
			key: 'full_name',
			value: user?.full_name || '',
			valid: true,
		},
		mobile_number: {
			key: 'mobile_number',
			value: user?.mobile_number || '',
			valid: true,
			disabled: +user?.mobile_number !== 91,
		},
		email_id: {
			key: 'email_id',
			value: user?.email_id || '',
			valid: true,
		},
		commodity: {
			key: 'commodity',
			value: user?.commodity || [],
			valid: true,
		},
		departments: {
			key: 'departments',
			value: user?.departments || [],
			valid: true,
		},
		role: {
			key: 'role',
			value: user?.role || [],
			valid: true,
		},
		region: {
			key: 'region',
			value: user?.region || '',
			valid: true,
		},
		monthly_target: {
			key: 'monthly_target',
			value: user?.monthly_target || 0,
			valid: true,
		},
		sub_role: {
			key: 'sub_role',
			value: user?.sub_role || 'PORTAL',
			valid: true,
		},
		country_code: {
			key: 'country_code',
			value: CountryCodes[0],
			valid: true,
		},
		platforms: {
			key: 'platforms',
			value: user?.platforms || [],
			valid: true,
		},
		employee_code: {
			key: 'employee_code',
			value: user?.employee_code ?? '',
			valid: true,
		},
		manager_name: {
			key: 'manager_name',
			value: user?.manager_name ?? '',
			valid: true,
		},
		oracle_id: {
			key: 'oracle_id',
			value: user?.oracle_id ?? '',
			valid: true,
		},
	}
	if (user) {
		_state.country_code.value = CountryCodes.find((item) => item.code === user?.country_code) || CountryCodes[0]
	}
	return _state
}
export const ADMIN_SOURCE_LIST = (ADMIN_SOURCE ?? []).map((item) => ({ key: item, label: item }))


const RequiredStar = styled.span`
	color: ${({ theme }) => theme.colors.status.error.text};
`

const FormGridWrapper = styled(GridListWrapper)`
	margin-bottom: ${Spacings.SPACING_2B};
`

const PhoneFieldWrapper = styled(BiFieldWrapper)`
	grid-template-columns: 1fr 2fr;
`

const requiredLabel = (label) => (
	<>
		{label} <RequiredStar>*</RequiredStar>
	</>
)

const AddAmdin = ({ user, update, getAllAdmins, newAdmin = false }) => {
	const loggedInuser = useSelector((_state) => _state.getIn(['user', 'user']))
	const dispatch = useDispatch()
	const userData = useSelector((_state) => _state.getIn(['root', 'drawerTypeMeta', 'user']))
	const [allCommodity, setAllCommodity] = useState([])

	// Only SUPER_ADMIN may change anything in this drawer — everyone else sees it read-only.
	const editDisabled = !isSuperAdminOnly(loggedInuser?.role)

	const _getAllAdmins = getAllAdmins?.map((item) => {
		item['display_name'] = `${(item.full_name || '') + ' '}(${item.mobile_number})`
		return item
	})
	const [details, setDetails] = useState(deriveStateFromProp())

	useEffect(() => {
		getAllCommodityApi().then((res) => {
			setAllCommodity(res)
		})
	}, [])

	const updateState = (type, key) => (value) => {
		if (typeof value === 'string') {
			value = value.trimStart()
		}
		if (type === 'role') {
			value = value.role.trimStart()
		}
		if (type === 'platforms') {
			value = value.map((item) => item.key)
		}
		if (type === 'departments') {
			value = value.map((item) => item.key)
		}
		setDetails((details) => ({
			...details,
			[type]: {
				...details[type],
				[key]: value,
			},
		}))
	}

	const disableButton = useMemo(() => {
		// All of these are mandatory on BOTH create and update.
		const requiredMissing =
			!details.full_name.value ||
			!details.email_id.value ||
			!details.mobile_number.value ||
			!details.mobile_number.valid ||
			!details.commodity.value?.length ||
			!details.region.value ||
			!details.manager_name.value ||
			!details.role.value ||
			!details.sub_role.value ||
			!details.employee_code.value ||
			!details.employee_code.valid
		// On update also keep the per-field format checks (e.g. valid email/mobile).
		return newAdmin ? requiredMissing : Object.values(details).some((d) => !d.valid) || requiredMissing
	}, [details])

	const returnFromValue = {
		departments: details.departments.value,
		commodity: details.commodity.value,
		country_code: details.country_code.value?.dial_code,
		email_id: details.email_id.value,
		employee_code: details.employee_code.value,
		full_name: details.full_name.value,
		manager_name: details.manager_name.value?.full_name,
		reporting_managers: [details.manager_name.value?.user_id],
		mobile_number: getCountryDialCode(details.country_code.value.code) + details.mobile_number.value,
		monthly_target: details.monthly_target.value,
		oracle_id: details.oracle_id.value,
		platforms: details.platforms.value,
		region: details.region.value,
		role: details.role.value,
		sub_role: details.sub_role.value,
	}

const AddNewAdmin = () => {
		dispatch(showLoader(true))
		createUser(returnFromValue, loggedInuser?.user_id)
			.then(() => {
				dispatch(showToast(true, 'Successfully created new admin user', { type: TOAST_TYPE.SUCCESS }))
				update()
				dispatch(toggleDrawer(false))
			})
			.catch((error) => {
				
				const errorMessage = error?.response?.data?.message ?? error?.data?.message ?? error?.message
				dispatch(showToast(true, errorMessage || strings('error something went wrong'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	let adminSubRoleTypes = Object.entries(ADMIN_SUB_ROLES).map(([key, value]) => ({ key, sub_role: value }))
	let businessCategoryTypes = Object.values(enquiryPlatform).map((i) => ({ key: i.name, label: i.name }))

	const renderAdminDetails = () => {
		let roles = Object.values(MB_ROLES)
			.map((i) => ({ role: i }))
			.sort((a, b) => {
				if (a.role < b.role) {
					return -1
				}
				if (a.role > b.role) {
					return 1
				}
				return 0
			})

		if (loggedInuser?.user_id !== SUPER_SUPER_USER_ID) {
			roles = roles.filter((i) => i.role !== MB_ROLES.SUPER_ADMIN && i.role !== MB_ROLES.ADMIN)
		}
		let regionTypes = Object.values(REGION_TYPES).map((i) => ({ region: i, disabled: i === REGION_TYPES.RAJASTHAN }))

		return (
			<>
				<EditDetailWrapper>
					<MultiSelect
						id='commodity_name'
						disabled={editDisabled}
						label={requiredLabel(strings('commodity', 'name'))}
						displayKey={'commodity_name'}
						primaryKey={'commodity_name'}
						placeholder={strings(['select', 'commodity', 'name'])}
						data={allCommodity}
						value={details.commodity?.value}
						onChange={updateState('commodity', 'value')}
						multiple
					/>
				</EditDetailWrapper>
				<FormGridWrapper>
					<Select
						label={requiredLabel(strings('region'))}
						id='region'
						data={regionTypes}
						type='text'
						value={{ region: details.region.value }}
						onChange={(value) => updateState('region', 'value')(value.region)}
						placeholder={strings('region')}
						displayKey='region'
						primaryKey='region'
						disabled={editDisabled}
					/>
					<Select
						id='select-manager'
						label={requiredLabel(strings('reporting', 'manager'))}
						placeholder={strings(['select', 'reporting', 'manager'])}
						data={_getAllAdmins}
						displayKey='display_name'
						primaryKey='user_id'
						value={details.manager_name.value || {}}
						disabled={editDisabled}
						onChange={(value) => updateState('manager_name', 'value')(value)}
					/>
				</FormGridWrapper>

				<FormGridWrapper itemPerRow={[2, 2, 2]}>
					<Select
						id='select-role'
						label={requiredLabel(strings('role'))}
						placeholder={strings('select', 'role')}
						data={roles}
						displayKey='role'
						primaryKey='role'
						value={{ role: details.role.value }}
						disabled={editDisabled}
						onChange={updateState('role', 'value')}
					/>

					<Select
						label={requiredLabel(strings('sub_role'))}
						id='sub_role'
						data={adminSubRoleTypes}
						type='text'
						value={adminSubRoleTypes.find((item) => item.key === details?.sub_role?.value) ?? {}}
						onChange={(value) => updateState('sub_role', 'value')(value.key)}
						placeholder={strings(['select', 'sub_role'])}
						displayKey='sub_role'
						primaryKey='key'
						disabled={editDisabled}
					/>
				</FormGridWrapper>
				<EditDetailWrapper>
					<Input
						id='monthly-target'
						type='number'
						placeholder={strings('0')}
						label={strings('monthly', 'target')}
						value={details?.monthly_target?.value}
						onChange={(e) => updateState('monthly_target', 'value')(e.target.value === '' ? '' : Math.abs(e.target.value))}
						disabled={editDisabled}
					/>
				</EditDetailWrapper>
				<EditDetailWrapper>
					<MultiSelect
						id='business-category'
						label={strings('business_category')}
						placeholder={strings('select', 'business_category')}
						value={details?.platforms?.value?.map((item) => ({ key: item, label: item })) ?? []}
						data={businessCategoryTypes}
						onChange={updateState('platforms', 'value')}
						primaryKey='key'
						displayKey='label'
						disabled={editDisabled}
					/>
				</EditDetailWrapper>
				<EditDetailWrapper>
					<Input
						id='employee-code'
						label={requiredLabel(strings('employee_code'))}
						placeholder={EMPLOYEE_CODE_PLACEHOLDER}
						pattern={EMPLOYEE_CODE_PATTERN}
						value={details?.employee_code?.value}
						error={
							details?.employee_code?.value && !details?.employee_code?.valid
								? strings('msg_invalid_employee_code')
								: undefined
						}
						onChange={(e) => {
							// typed in lower case is the same code, so it is stored upper-cased
							const employeeCode = e.target.value.toUpperCase()
							updateState('employee_code', 'value')(employeeCode)
							updateState('employee_code', 'valid')(EMPLOYEE_CODE_REGEX.test(employeeCode))
						}}
						disabled={editDisabled}
					/>
				</EditDetailWrapper>
				<EditDetailWrapper>
					<Input
						id='oracle-id'
						label={strings('oracle_id')}
						placeholder={strings('enter', 'oracle_id')}
						value={details?.oracle_id?.value}
						onChange={(e) => updateState('oracle_id', 'value')(e.target.value)}
						disabled={editDisabled}
					/>
				</EditDetailWrapper>
			</>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<EditDetailWrapper>
					<Input
						id='user-name'
						type='text'
						placeholder={strings('your_full_name')}
						label={requiredLabel(strings('name'))}
						value={details?.full_name?.value}
						onChange={(e) => updateState('full_name', 'value')(e.target.value)}
						disabled={editDisabled}
					/>
				</EditDetailWrapper>
				<EditDetailWrapper>
					<Input
						type='email'
						pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
						label={requiredLabel(strings('email'))}
						placeholder={strings('your_email_address')}
						setValidity={updateState('email_id', 'valid')}
						errorMap={{
							patternMismatch: `${strings('enter_valid_email')}`,
						}}
						value={details?.email_id?.value}
						onChange={(e) => {
							updateState('email_id', 'valid')(e.target.validity.valid)
							updateState('email_id', 'value')(e.target.value)
						}}
						disabled={editDisabled}
					/>
				</EditDetailWrapper>
				<PhoneFieldWrapper>
					<Select
						id={'country-code'}
						label={strings('code')}
						data={CountryCodes}
						displayKey='dial_code'
						primaryKey='name'
						value={CountryCodes.find((item) => details?.country_code?.value?.code === item.code) ?? CountryCodes[0]}
						onChange={(val) => updateState('country_code', 'value')(val)}
						type='tel'
						noDropIcon
						disabled={editDisabled}
					/>
					<Input
						id='user-mobile'
						label={requiredLabel(strings('mobile'))}
						type='tel'
						placeholder={strings('enter_your_mobile_num')}
						maxLength={12}
						minLength={!details?.mobile_number?.disabled && 7}
						pattern='^\d{7}\d+$'
						value={details?.mobile_number?.value}
						onChange={(e) => {
							updateState('mobile_number', 'valid')(e.target.validity.valid)
							updateState('mobile_number', 'value')(e.target.value)
						}}
						disabled={
							editDisabled ||
							(12 === details?.mobile_number?.value?.length &&
								details?.mobile_number.disabled &&
								!isSuperAdmin(loggedInuser?.role) &&
								userData?.mobile_number?.length === 12)
						}
						errorMap={{
							patternMismatch: `${strings('enter_valid_num')}`,
							valueMissing: `${strings('mob_num_required')}`,
						}}
						setValidity={updateState('mobile_number', 'valid')}
					/>
				</PhoneFieldWrapper>
				{/* {isClient(user?.role) && ( */}
				<EditDetailWrapper>
					<MultiSelect
						id='departments'
						disabled={editDisabled}
						label={strings('admin', 'source')}
						primaryKey='key'
						displayKey='label'
						placeholder={strings(['select', 'admin', 'source'])}
						data={ADMIN_SOURCE_LIST}
						// value={details.departments?.value}
						value={details?.departments?.value?.map((item) => ({ key: item, label: item })) ?? []}
						onChange={updateState('departments', 'value')}
						multiple
					/>
				</EditDetailWrapper>
				{/* )} */}

				{/* {isAdminUser && renderAdminDetails()} */}
				{renderAdminDetails()}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button disabled={disableButton || editDisabled} onClick={AddNewAdmin}>
					{strings('add')}
				</Button>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddAmdin
